import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"

import {
  replaceScheduleEvent,
  updateScheduleEvent,
  validateScheduleEventAction,
  type CalendarBoardPropertyRef,
  type CalendarBoardWorkOrderRef,
} from "../../../../../../../../../api/kehrblatt/schedule-events"
import { confirmScheduleEventActionIssues } from "../../../../../../../../../_shared/_logic/confirm-schedule-event-action-issues"
import type { WorkingHoursByUser } from "../../../../../../../../../_shared/_logic/form-state"
import type {
  EmployeeOption,
  EventUpdatePayload,
} from "../../../../../../../_shared/_logic/calendar-types"

type CalendarEventUpdateQueueState = {
  latestActionId: number
  chain: Promise<void | boolean>
  confirmedEvent: ScheduleEvent
}

export type CalendarEventUpdateQueue = {
  nextActionId: number
  states: Map<string, CalendarEventUpdateQueueState>
}

type CalendarEventUpdateAction = {
  actionId: number
  eventId: string
  patch: ScheduleEventUpdatePayload
}

export type CalendarUpdateCommandContext = {
  organizationId: string | null
  now: Date
  scheduleEvents: ScheduleEvent[]
  workingHoursByUser: WorkingHoursByUser
  employees: EmployeeOption[]
  workOrders: CalendarBoardWorkOrderRef[]
  properties: CalendarBoardPropertyRef[]
  upsertEvent: (event: ScheduleEvent) => void
  refreshEvents: () => Promise<void>
  eventUpdateQueue: CalendarEventUpdateQueue
}

const GENERIC_VALIDATION_ERROR = "Bitte prüfen Sie die markierten Felder."
const VALIDATION_ACTION_ERROR =
  "Warnprüfung konnte nicht durchgeführt werden. Änderung wurde zurückgesetzt."

export const createCalendarEventUpdateQueue = (): CalendarEventUpdateQueue => ({
  nextActionId: 0,
  states: new Map(),
})

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  const message = error instanceof Error ? error.message?.trim() : ""

  if (message === GENERIC_VALIDATION_ERROR) {
    return VALIDATION_ACTION_ERROR
  }

  return message || fallback
}

const findCurrentEvent = (context: CalendarUpdateCommandContext, event: ScheduleEvent) =>
  context.scheduleEvents.find((entry) => entry.id === event.id) ?? event

const employeeName = (context: CalendarUpdateCommandContext, employeeId: string | null) => {
  if (!employeeId) return null

  return context.employees.find((employee) => employee.id === employeeId)?.label ?? null
}

const buildPatch = (
  event: ScheduleEvent,
  start: Date,
  end: Date,
  employeeId: string | null,
  flexType?: ScheduleEventFlexType,
  timeframeStart?: Date | null,
  timeframeEnd?: Date | null,
): ScheduleEventUpdatePayload => {
  const targetFlexType = flexType ?? (event.flex_type as ScheduleEventFlexType)
  const patch: ScheduleEventUpdatePayload = {
    start_at: start.toISOString(),
    end_at: end.toISOString(),
    employee_id: employeeId,
  }

  if (flexType !== undefined) {
    patch.flex_type = flexType
  }

  if (targetFlexType === "SOFT") {
    patch.timeframe_start_at = (timeframeStart ?? start).toISOString()
    patch.timeframe_end_at = (timeframeEnd ?? end).toISOString()
  } else {
    patch.timeframe_start_at = start.toISOString()
    patch.timeframe_end_at = end.toISOString()
  }

  return patch
}

const buildOptimisticEvent = (
  context: CalendarUpdateCommandContext,
  event: ScheduleEvent,
  patch: ScheduleEventUpdatePayload,
): ScheduleEvent => {
  const employeeId =
    patch.employee_id === undefined ? event.employee_id : (patch.employee_id ?? null)

  return {
    ...event,
    start_at: patch.start_at ?? event.start_at,
    end_at: patch.end_at ?? event.end_at,
    employee_id: employeeId,
    employee_name: employeeName(context, employeeId) ?? event.employee_name ?? null,
    flex_type: patch.flex_type ?? event.flex_type,
    timeframe_start_at:
      patch.timeframe_start_at === undefined
        ? event.timeframe_start_at
        : (patch.timeframe_start_at ?? null),
    timeframe_end_at:
      patch.timeframe_end_at === undefined
        ? event.timeframe_end_at
        : (patch.timeframe_end_at ?? null),
  }
}

const isLatestAction = (state: CalendarEventUpdateQueueState, action: CalendarEventUpdateAction) =>
  state.latestActionId === action.actionId

const revertLatestAction = (
  context: CalendarUpdateCommandContext,
  state: CalendarEventUpdateQueueState,
  action: CalendarEventUpdateAction,
) => {
  if (!isLatestAction(state, action)) return

  context.upsertEvent(state.confirmedEvent)
}

const applyServerResult = (
  context: CalendarUpdateCommandContext,
  state: CalendarEventUpdateQueueState,
  action: CalendarEventUpdateAction,
  validation: ScheduleEventActionValidationResponse,
  result: ScheduleEvent | ScheduleEventReplaceResponse,
): boolean => {
  if (validation.execution_strategy === "CANCEL_AND_CREATE") {
    const replacement = result as ScheduleEventReplaceResponse
    state.confirmedEvent = replacement.canceled_event

    if (!isLatestAction(state, action)) return false

    context.upsertEvent(replacement.canceled_event)
    context.upsertEvent(replacement.created_event)

    return true
  }

  const updated = result as ScheduleEvent
  state.confirmedEvent = updated

  if (!isLatestAction(state, action)) return false

  context.upsertEvent(updated)

  return false
}

const refreshProjectionAfterReplacement = async (context: CalendarUpdateCommandContext) => {
  try {
    await context.refreshEvents()
  } catch (error) {
    console.error("calendar_replacement_projection_refresh_failed", error)
  }
}

const executeQueuedCalendarEventUpdate = async (
  context: CalendarUpdateCommandContext,
  state: CalendarEventUpdateQueueState,
  action: CalendarEventUpdateAction,
) => {
  if (!isLatestAction(state, action)) return false

  try {
    const validation = await validateScheduleEventAction({
      operation: "UPDATE",
      surface: "CALENDAR",
      event_id: action.eventId,
      update_payload: action.patch,
    })

    if (!isLatestAction(state, action)) return false

    if (validation.issues.length) {
      const confirmed = await confirmScheduleEventActionIssues(validation.issues, {
        operation: "UPDATE",
        surface: "CALENDAR",
        executionStrategy: validation.execution_strategy,
      })

      if (!isLatestAction(state, action)) return false

      if (!confirmed) {
        revertLatestAction(context, state, action)

        return false
      }
    }

    const result =
      validation.execution_strategy === "CANCEL_AND_CREATE"
        ? await replaceScheduleEvent(action.eventId, action.patch)
        : await updateScheduleEvent(action.eventId, action.patch)
    const shouldRefreshProjection = applyServerResult(context, state, action, validation, result)

    if (shouldRefreshProjection && isLatestAction(state, action)) {
      await refreshProjectionAfterReplacement(context)
    }

    return true
  } catch (error) {
    if (!isLatestAction(state, action)) return false

    revertLatestAction(context, state, action)
    console.error("schedule_event_update_failed", error)
    newNotification({
      class: "error",
      html: resolveErrorMessage(error, VALIDATION_ACTION_ERROR),
    })

    return false
  }
}

export const updateCalendarEventFromBoard = (
  context: CalendarUpdateCommandContext,
  { event, start, end, employeeId, flexType, timeframeStart, timeframeEnd }: EventUpdatePayload,
) => {
  const organizationId = context.organizationId

  if (!organizationId) return

  const targetEmployeeId = employeeId ?? event.employee_id ?? null

  if (start >= end) {
    newNotification({ class: "error", html: "Startzeit muss vor der Endzeit liegen." })

    return
  }

  const queue = context.eventUpdateQueue
  const currentEvent = findCurrentEvent(context, event)
  const patch = buildPatch(
    currentEvent,
    start,
    end,
    targetEmployeeId,
    flexType,
    timeframeStart,
    timeframeEnd,
  )
  const optimisticEvent = buildOptimisticEvent(context, currentEvent, patch)
  const existingState = queue.states.get(event.id)
  const state =
    existingState ??
    ({
      latestActionId: 0,
      chain: Promise.resolve(),
      confirmedEvent: currentEvent,
    } satisfies CalendarEventUpdateQueueState)
  const action: CalendarEventUpdateAction = {
    actionId: ++queue.nextActionId,
    eventId: event.id,
    patch,
  }
  state.latestActionId = action.actionId
  queue.states.set(event.id, state)
  context.upsertEvent(optimisticEvent)

  state.chain = state.chain
    .catch(() => undefined)
    .then(() => executeQueuedCalendarEventUpdate(context, state, action))
    .finally(() => {
      const currentState = queue.states.get(event.id)

      if (currentState === state && state.latestActionId === action.actionId) {
        queue.states.delete(event.id)
      }
    })

  return state.chain
}
